import {
  ProviderUnavailableError,
  type ImageEditProvider,
  type ImageEditRequest,
  type ImageEditResult,
  type ProviderCallOptions,
  type ProviderImageEditJobState,
  type ProviderJobHandle,
} from "@loumy/contracts";

// §7 `image-edit`-Provider (D50/D76). Zwei Adapter:
//   • FalQwenImageEditProvider — ECHT, Default: Qwen Image 2.0 EDIT-Endpoint über fal.ai
//     (generate+edit = EIN Modell, derselbe FAL_API_KEY wie image/video/vision). Versorgt
//     `input-normalize` (D50) und die D76-Edit-Schleife (validate→edit→re-validate).
//   • ConfigurableImageEditProvider — registrierbarer Swap (eigener Endpoint, §13).
// Beide ENV-gated: ohne Key werfen generate()/poll() definiert. Provenance REQUIRED (D39).
//
// D27 (umgestellt vom synchronen Einzelaufruf wie beim VACE-Inpainting, D141): generate()
// submittet NUR → Handle (status_url/response_url in raw); poll() ist EIN schneller Status-GET
// (KEIN blockierender SSE-Stream — der Runner-Treiber muss nach dem Tick-Budget pausieren
// können). Erschöpft das Pro-Tick-Budget den Poll, pollt ein Resume-Tick dasselbe Handle weiter
// (kein Re-Submit). Das löst den `fal qwen-edit: nicht fertig nach … Polls`-Job-Fail bei voller
// fal-Queue strukturell (Queue-Wartezeit wird tick-übergreifend statt tick-intern).

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_QWEN_EDIT_MODEL = "fal-ai/qwen-image-2/edit";

/**
 * poll()-intern (Codex-P1-Analog zu D141): ein TRANSIENTER Fehler beim Ergebnis-Fetch NACH
 * `COMPLETED` (fal-Result-GET 429/5xx). Der Edit-Job IST fertig, nur der Last-Mile-Fetch
 * flackerte — poll() meldet dann `processing` (Retry im nächsten Poll/Tick) statt den fertigen
 * Job terminal zu killen. Terminale 4xx / fehlendes images[].url bleiben Hard-Fail.
 */
class ImageEditResultTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImageEditResultTransientError";
  }
}

export interface ImageEditProviderConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Edit-Endpoint-Override (Default `fal-ai/qwen-image-2/edit`). */
  model?: string;
  /** Qwens serverseitige Prompt-Expansion (Default false — wir liefern eine präzise Instruktion). */
  promptExpansion?: boolean;
  /** Qwens Safety-Checker (Default true, fail-safe). */
  safetyChecker?: boolean;
}

/**
 * §7 image-edit → Qwen Image 2.0 EDIT über fal.ai (D76, D27-Handle). `generate({imageUrl,
 * instruction})` → submit (image_urls=[imageUrl], prompt=instruction) → Handle (status_url/
 * response_url); `poll(handle)` → ein Status-GET, bei COMPLETED editiertes Bild laden.
 * Der Resume-Treiber (runImageEditProviderJob) pausiert/pollt über Tick-Grenzen.
 */
export class FalQwenImageEditProvider implements ImageEditProvider {
  readonly id = "fal-qwen-image-edit";
  readonly version = 1;
  readonly displayName = "fal.ai (Qwen Image 2.0 Edit)";

  private readonly baseUrl: string;
  private readonly model: string;
  private readonly promptExpansion: boolean;
  private readonly safetyChecker: boolean;

  constructor(private readonly config: ImageEditProviderConfig = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_QWEN_EDIT_MODEL;
    this.promptExpansion = config.promptExpansion ?? false;
    this.safetyChecker = config.safetyChecker ?? true;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("image-edit", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift, wie die übrigen fal-Adapter). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal qwen-edit: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal qwen-edit: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal qwen-edit: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /**
   * D27: NUR submitten → Handle (status_url/response_url in `raw`, DB-persistiert). KEIN Poll/
   * Fetch hier — der Treiber persistiert das Handle VOR dem ersten poll(), damit ein Resume-Tick
   * weiterpollt statt neu zu submitten. `opts.signal` bricht den Submit ab.
   */
  async generate(req: ImageEditRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        prompt: req.instruction,
        image_urls: [req.imageUrl],
        num_images: 1,
        output_format: "png",
        enable_prompt_expansion: this.promptExpansion,
        enable_safety_checker: this.safetyChecker,
      }),
      signal: opts?.signal,
    });
    if (!submit.ok) {
      const body = submit.status < 500 ? (await submit.text().catch(() => "")).slice(0, 500) : "";
      throw new Error(`fal qwen-edit submit: HTTP ${submit.status} ${submit.statusText}${body ? ` — ${body}` : ""}`);
    }
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal qwen-edit submit: keine request_id in der Antwort.");
    const statusUrl = this.requireFalUrl(sub.status_url, "status_url");
    const responseUrl = this.requireFalUrl(sub.response_url, "response_url");
    return {
      providerId: this.id,
      providerVersion: this.version,
      externalId: sub.request_id,
      // Poll-Koordinaten (DB-jsonb-persistiert, Resume-stabil). poll() validiert sie ERNEUT
      // gegen den fal-Host (das Handle kommt beim Resume aus der DB).
      raw: { statusUrl, responseUrl },
    };
  }

  /**
   * D27: EIN schneller Status-GET (kein blockierender Stream — der Tick-Treiber muss pausieren
   * können). `IN_QUEUE`→pending, `IN_PROGRESS`→processing, `COMPLETED`→editiertes Bild laden→done.
   * Transiente HTTP-Fehler (429/5xx) → processing (späterer Tick pollt weiter); terminale 4xx /
   * fal-error / unerwarteter Status / fehlendes images[].url → failed.
   */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageEditJobState> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const raw = (handle.raw ?? {}) as { statusUrl?: unknown; responseUrl?: unknown };
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url (handle)");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url (handle)");

    const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!statusRes.ok) {
      if (statusRes.status === 429 || statusRes.status >= 500) return { status: "processing" };
      const body = (await statusRes.text().catch(() => "")).slice(0, 500);
      return { status: "failed", error: `fal qwen-edit status: HTTP ${statusRes.status} ${statusRes.statusText}${body ? ` — ${body}` : ""}` };
    }
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: `fal qwen-edit: ${status.error}` };
    switch (status.status) {
      case "COMPLETED":
        try {
          return { status: "done", output: await this.fetchResult(responseUrl, apiKey, handle.externalId, signal) };
        } catch (e) {
          if (e instanceof ImageEditResultTransientError) return { status: "processing" }; // Retry nächster Tick
          throw e;
        }
      case "IN_QUEUE":
        return { status: "pending" };
      case "IN_PROGRESS":
        return { status: "processing" };
      default:
        return { status: "failed", error: `fal qwen-edit: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).` };
    }
  }

  /** COMPLETED → editiertes Bild laden. Transienter Result-Flap (429/5xx) → ImageEditResultTransientError
   *  (poll → processing/Retry); terminale 4xx / fehlendes images[].url → harter Fehler. */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<ImageEditResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) {
      if (resultRes.status === 429 || resultRes.status >= 500)
        throw new ImageEditResultTransientError(`fal qwen-edit result: HTTP ${resultRes.status} (transient)`);
      const body = (await resultRes.text().catch(() => "")).slice(0, 500);
      throw new Error(`fal qwen-edit result: HTTP ${resultRes.status} ${resultRes.statusText}${body ? ` — ${body}` : ""}`);
    }
    const result = (await resultRes.json()) as { images?: { url?: unknown }[]; seed?: unknown };
    const url = result.images?.find((im) => typeof im?.url === "string")?.url;
    if (typeof url !== "string") throw new Error("fal qwen-edit: COMPLETED, aber kein images[].url im Ergebnis.");
    return {
      imageUrl: url,
      provenance: {
        provider: this.id,
        modelVersion: this.model,
        requestId,
        seed: typeof result.seed === "number" ? result.seed : null,
      },
    };
  }
}

/** Registrierbarer Swap (eigener Image-Edit-Endpoint, §13). Ohne Key definiert werfen. */
export class ConfigurableImageEditProvider implements ImageEditProvider {
  readonly id = "image-edit-default";
  readonly version = 1;
  readonly displayName = "Image-Edit (PLACEHOLDER §13/D50)";

  constructor(private readonly config: ImageEditProviderConfig = {}) {}

  // TODO(§13): eigenen Image-Edit-Endpoint verdrahten (FLUX-edit/SDXL-inpaint). Default = Qwen (s.o.).
  private unavailable(): never {
    throw new ProviderUnavailableError(
      "image-edit",
      this.id,
      this.config.apiKey
        ? "Konfigurierbarer Image-Edit-Endpoint offen (§13) — Default ist FalQwenImageEditProvider."
        : "IMAGE_EDIT_API_KEY fehlt (Adapter nicht konfiguriert, §13).",
    );
  }

  async generate(_req: ImageEditRequest, _opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    this.unavailable();
  }

  async poll(_handle: ProviderJobHandle, _opts?: ProviderCallOptions): Promise<ProviderImageEditJobState> {
    this.unavailable();
  }
}
